import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Building2, Calendar, Instagram } from "lucide-react";

import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import UtmBanner from "@/components/landing/UtmBanner";
import Footer from "@/components/landing/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/landing/Breadcrumbs";
import { BUSINESS } from "@/data/business";

export const OverOns = () => {
  const breadcrumbs = [{ name: "Over ons", path: "/over-ons" }];
  return (
    <main data-testid="page-over-ons" className="bg-background">
      <Seo
        title="Over EquiSight — ons verhaal, missie & team"
        description="EquiSight is een Nederlands paarden management platform, opgericht in 2026 door Martijn Wirken. Onderdeel van SaFleu Equestrian Centre BV in Raamsdonk."
        path="/over-ons"
        keywords={["over equisight", "wie is equisight", "paarden app nederland"]}
        breadcrumbs={breadcrumbs}
      />
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      <Breadcrumbs items={breadcrumbs} />

      <section className="mx-auto max-w-3xl px-6 py-16 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
          Over ons
        </p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] text-balance sm:text-5xl lg:text-6xl">
          EquiSight — het centrale brein voor paard & ruiter.
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
          EquiSight is een Nederlands paarden management platform. Ontwikkeld
          door en voor mensen rond het paard — eigenaren, ruiters, trainers,
          hoefsmeden en dierenartsen. Eén centraal dossier per paard, met
          slimme inzichten van EquiCoach.
        </p>

        <div className="mt-12 space-y-12 text-[hsl(var(--ink))]">
          <section>
            <h2 className="font-display text-2xl font-medium leading-snug sm:text-3xl">
              Wat is EquiSight?
            </h2>
            <p className="mt-4 leading-relaxed text-[hsl(var(--ink-soft))]">
              EquiSight is een mobiele applicatie voor iOS en Android waarmee
              je gezondheid, training, voeding, planning en wedstrijdresultaten
              van paarden centraal beheert. EquiCoach — de intelligence binnen
              EquiSight — geeft je inzicht in trends, signalen en concreet
              advies op basis van je eigen data.
            </p>
            <p className="mt-4 leading-relaxed text-[hsl(var(--ink-soft))]">
              EquiSight verbindt eigenaren met hun trainer, hoefsmid, tandarts,
              fysio en dierenarts. Iedereen rond het paard werkt vanuit
              hetzelfde dossier — geen WhatsApp-chaos meer, geen losse
              schoenendoos met formulieren.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-medium leading-snug sm:text-3xl">
              Onze missie
            </h2>
            <p className="mt-4 leading-relaxed text-[hsl(var(--ink-soft))]">
              Paardenwelzijn en sportieve prestaties verbeteren door data
              toegankelijk en bruikbaar te maken voor iedere ruiter en
              eigenaar — niet alleen voor topsport. Insight in every stride.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-medium leading-snug sm:text-3xl">
              Het team
            </h2>
            <p className="mt-4 leading-relaxed text-[hsl(var(--ink-soft))]">
              EquiSight is opgericht in {BUSINESS.foundingDate} door{" "}
              <strong className="text-[hsl(var(--ink))]">
                {BUSINESS.founder.name}
              </strong>{" "}
              vanuit{" "}
              <strong className="text-[hsl(var(--ink))]">
                {BUSINESS.legalName}
              </strong>{" "}
              in Raamsdonk, Nederland. We zijn een klein team van ruiters,
              ontwikkelaars en designers met één gemeenschappelijk doel:
              paardenmanagement digitaliseren zonder de menselijke maat te
              verliezen.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-medium leading-snug sm:text-3xl">
              Waar staan we voor?
            </h2>
            <ul className="mt-4 space-y-2 text-[hsl(var(--ink-soft))]">
              {[
                "Paardenwelzijn boven alles",
                "Eigenaar bepaalt wie wat ziet — privacy first",
                "Data binnen de EU, AVG-conform",
                "Geen marketing-trucks: eerlijke prijzen, maandelijks opzegbaar",
                "Bouwen vanuit de stal, niet vanuit een kantoor",
              ].map((li) => (
                <li key={li} className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--sand-dark))]" />
                  <span>{li}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))] p-7">
            <h2 className="font-display text-xl font-medium leading-snug text-[hsl(var(--ink))]">
              Bedrijfsgegevens
            </h2>
            <dl className="mt-6 space-y-3 text-sm text-[hsl(var(--ink-soft))]">
              <div className="flex items-start gap-3">
                <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--sand-dark))]" strokeWidth={1.8} />
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">Statutaire naam</dt>
                  <dd className="text-[hsl(var(--ink))]">{BUSINESS.legalName}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--sand-dark))]" strokeWidth={1.8} />
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">Adres</dt>
                  <dd className="text-[hsl(var(--ink))]">
                    {BUSINESS.address.streetAddress}, {BUSINESS.address.postalCode}{" "}
                    {BUSINESS.address.addressLocality}, Nederland
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--sand-dark))]" strokeWidth={1.8} />
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">Opgericht</dt>
                  <dd className="text-[hsl(var(--ink))]">{BUSINESS.foundingDate}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--sand-dark))]" strokeWidth={1.8} />
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">KvK</dt>
                  <dd className="text-[hsl(var(--ink))]">{BUSINESS.kvk}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--sand-dark))]" strokeWidth={1.8} />
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">BTW</dt>
                  <dd className="text-[hsl(var(--ink))]">{BUSINESS.vatID}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--sand-dark))]" strokeWidth={1.8} />
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">Email</dt>
                  <dd>
                    <a href={`mailto:${BUSINESS.email}`} className="text-[hsl(var(--ink))] underline-offset-4 hover:underline">
                      {BUSINESS.email}
                    </a>
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Instagram className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--sand-dark))]" strokeWidth={1.8} />
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">Instagram</dt>
                  <dd>
                    <a
                      href="https://www.instagram.com/equi.sight/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[hsl(var(--ink))] underline-offset-4 hover:underline"
                    >
                      @equi.sight
                    </a>
                  </dd>
                </div>
              </div>
            </dl>
          </section>

          <p className="text-[hsl(var(--ink-soft))]">
            Meer weten? Bekijk onze{" "}
            <Link to="/functies" className="border-b border-[hsl(var(--ink))]/30 text-[hsl(var(--ink))] hover:border-[hsl(var(--ink))]">
              functies
            </Link>
            ,{" "}
            <Link to="/kennisbank" className="border-b border-[hsl(var(--ink))]/30 text-[hsl(var(--ink))] hover:border-[hsl(var(--ink))]">
              kennisbank
            </Link>{" "}
            of{" "}
            <Link to="/contact" className="border-b border-[hsl(var(--ink))]/30 text-[hsl(var(--ink))] hover:border-[hsl(var(--ink))]">
              neem contact op
            </Link>
            .
          </p>
        </div>
      </section>

      <FinalCTA />
      <Footer />
    </main>
  );
};

export const Contact = () => {
  const breadcrumbs = [{ name: "Contact", path: "/contact" }];
  return (
    <main data-testid="page-contact" className="bg-background">
      <Seo
        title="Contact EquiSight — adres, email & support"
        description="Contactgegevens van EquiSight: support@equisight.nl. SaFleu Equestrian Centre BV, Kerklaan 7B 4944 VA Raamsdonk. KvK 97318159 · BTW NL868000164B01."
        path="/contact"
        keywords={["equisight contact", "equisight adres", "equisight support"]}
        breadcrumbs={breadcrumbs}
      />
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      <Breadcrumbs items={breadcrumbs} />

      <section className="mx-auto max-w-3xl px-6 py-16 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
          Contact
        </p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] text-balance sm:text-5xl lg:text-6xl">
          We zijn bereikbaar.
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
          Vragen, samenwerkingen of feedback? Mail ons direct — we reageren
          binnen 24 uur op werkdagen.
        </p>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <a
            href={`mailto:${BUSINESS.email}`}
            data-testid="contact-email"
            className="flex items-start gap-4 rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-7 transition-all hover:border-[hsl(var(--forest))]"
          >
            <Mail className="h-6 w-6 text-[hsl(var(--forest))]" strokeWidth={1.5} />
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">Email</p>
              <p className="mt-1 font-display text-lg text-[hsl(var(--ink))]">{BUSINESS.email}</p>
              <p className="mt-2 text-xs text-[hsl(var(--ink-soft))]">Antwoord binnen 24 uur op werkdagen</p>
            </div>
          </a>
          <a
            href="https://www.instagram.com/equi.sight/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-instagram"
            className="flex items-start gap-4 rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-7 transition-all hover:border-[hsl(var(--forest))]"
          >
            <Instagram className="h-6 w-6 text-[hsl(var(--forest))]" strokeWidth={1.5} />
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">Instagram</p>
              <p className="mt-1 font-display text-lg text-[hsl(var(--ink))]">@equi.sight</p>
              <p className="mt-2 text-xs text-[hsl(var(--ink-soft))]">Volg ons voor updates en sneak peeks</p>
            </div>
          </a>
        </div>

        <div className="mt-10 rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))] p-7">
          <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
            Bezoek- en postadres
          </p>
          <p className="mt-4 font-display text-2xl text-[hsl(var(--ink))]">
            {BUSINESS.legalName}
          </p>
          <p className="mt-2 text-[hsl(var(--ink-soft))]">
            {BUSINESS.address.streetAddress}
            <br />
            {BUSINESS.address.postalCode} {BUSINESS.address.addressLocality}
            <br />
            Nederland
          </p>
          <dl className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
            <div>
              <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">KvK</dt>
              <dd className="text-[hsl(var(--ink))]">{BUSINESS.kvk}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">BTW</dt>
              <dd className="text-[hsl(var(--ink))]">{BUSINESS.vatID}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">Opgericht</dt>
              <dd className="text-[hsl(var(--ink))]">{BUSINESS.foundingDate}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">Land</dt>
              <dd className="text-[hsl(var(--ink))]">Nederland</dd>
            </div>
          </dl>
        </div>
      </section>

      <FinalCTA />
      <Footer />
    </main>
  );
};
